import { Item } from "@/types/productTypes";
import { produce } from "immer";
import { StateCreator } from "zustand";

import fetchDb from "../utils/customRequestFunction";
import { getItem, getOption } from "../utils/getFunction";

import { AdminStoreState } from "./useAdminStore";

export interface ItemStoreState {
   selectedItemId: number | null;
   addItem: (
      productId: number,
      stageId: number,
      optionId: number
   ) => Promise<void>;
   updateItem: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      field: keyof Item,
      value: any
   ) => Promise<void>;
   deleteItem: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number
   ) => Promise<void>;
   reorderItems: (
      productId: number,
      stageId: number,
      optionId: number,
      id1: number,
      id2: number
   ) => Promise<void>;
   setSelectedItemId: (id: number | null) => void;
   selectItem: (stageId: number, optionId: number, itemId: number) => void;
}

export const createItemSlice: StateCreator<
   Partial<AdminStoreState> & ItemStoreState
> = (set, get) => ({
   selectedItemId: null,
   addItem: async (productId, stageId, optionId) => {
      try {
         const allProducts = get().allProducts;
         if (!allProducts) return;

         const option = getOption(allProducts, productId, stageId, optionId);
         if (!option) return;

         const newItemData = {
            name: "New Item",
            price: 0,
            idName: "new-item-id",
            optionId: optionId,
            relativePos: option.items?.length || 0,
            priceDepends: false,
         };

         const createdItem = await fetchDb.post("/api/items", newItemData);

         set(
            produce((state) => {
               const opt = getOption(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId
               );
               if (!opt) return;
               if (!opt.items) opt.items = [];
               opt.items.push(createdItem);
            })
         );
         if (createdItem.id) {
            get().selectItem(stageId, optionId, createdItem.id);
         }
      } catch (error) {
         console.error("Error adding item:", error);
      }
   },

   updateItem: async (productId, stageId, optionId, itemId, field, value) => {
      set(
         produce((state) => {
            const item = getItem(
               state.allProducts,
               productId,
               stageId,
               optionId,
               itemId
            );
            if (item) {
               (item as any)[field] = value;
            }
         })
      );

      try {
         await fetchDb.patch(`/api/items/${itemId}`, { [field]: value });
      } catch (error) {
         console.error("Error updating item:", error);
      }
   },

   deleteItem: async (productId, stageId, optionId, itemId) => {
      set(
         produce((state) => {
            const opt = getOption(
               state.allProducts,
               productId,
               stageId,
               optionId
            );
            if (opt?.items) {
               opt.items = opt.items.filter((i) => i.id !== itemId);
            }
         })
      );

      try {
         await fetchDb.delete(`/api/items/${itemId}`);
      } catch (error) {
         console.error("Error deleting item:", error);
      }
   },

   reorderItems: async (productId, stageId, optionId, id1, id2) => {
      set(
         produce((state) => {
            const opt = getOption(
               state.allProducts,
               productId,
               stageId,
               optionId
            );
            if (!opt || !opt.items) return;

            const item1 = opt.items.find((it) => it.id === id1);
            const item2 = opt.items.find((it) => it.id === id2);
            if (item1 && item2) {
               [item1.relativePos, item2.relativePos] = [
                  item2.relativePos,
                  item1.relativePos,
               ];
               opt.items.sort((a, b) => a.relativePos - b.relativePos);
            }
         })
      );

      try {
         await fetchDb.patch(`/api/items/reorder/${id1}/${id2}`);
      } catch (error) {
         console.error("Error reordering items:", error);
      }
   },
   setSelectedItemId: (id) => {
      set(
         produce((state) => {
            state.selectedItemId = id;
         })
      );
   },

   selectItem: (stageId, optionId, itemId) => {
      set(
         produce((state) => {
            state.selectedStageId = stageId;
            state.selectedOptionId = optionId;
            state.selectedItemId = itemId;
         })
      );
   },
});
